import { type RefObject, useEffect } from "react";

/**
 * Hook to auto-grow textarea based on content.
 *
 * Automatically adjusts textarea height as user types, with min/max constraints.
 * Useful for creating dynamic, expanding text input areas.
 *
 * @param textareaRef - Ref to the textarea element
 * @param minHeight - Minimum height in pixels (default: 100)
 * @param maxHeight - Maximum height in pixels (default: 400)
 */
export function useTextareaAutoGrow(
	textareaRef: RefObject<HTMLTextAreaElement | null>,
	minHeight = 100,
	maxHeight = 400
) {
	useEffect(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;

		// Reset height to recalculate
		textarea.style.height = "auto";

		// Set new height based on scrollHeight, with min and max constraints
		const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
		textarea.style.height = `${newHeight}px`;
	});
}
